import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getFreeTrialConfig, updateFreeTrialConfig } from "@/api/subscriptions";
import { api } from "@/api/client";
import type { Exam } from "@/types";

export default function AdminFreeTrial() {
  const queryClient = useQueryClient();
  const [selectedExamIds, setSelectedExamIds] = useState<string[]>([]);
  const [questionCount, setQuestionCount] = useState<number>(50);
  const [initialised, setInitialised] = useState(false);

  const { isLoading: configLoading } = useQuery({
    queryKey: ["free-trial-config"],
    queryFn: getFreeTrialConfig,
    onSuccess: (data: any) => {
      if (!initialised) {
        setSelectedExamIds(data.exam_ids ?? []);
        setQuestionCount(data.question_count ?? 50);
        setInitialised(true);
      }
    },
  } as any);

  const { data: exams = [], isLoading: examsLoading } = useQuery<Exam[]>({
    queryKey: ["exams-all"],
    queryFn: () => api.get<Exam[]>("/exams"),
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      updateFreeTrialConfig({ question_count: questionCount, exam_ids: selectedExamIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["free-trial-config"] });
      alert("Free trial config saved.");
    },
  });

  const toggleExam = (id: string) => {
    setSelectedExamIds((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]
    );
  };

  if (configLoading || examsLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        Loading…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Free Trial Configuration</h1>
      <p className="text-gray-500 text-sm mb-8">
        Configure which mock exams are used as the question pool for the free trial exam.
        If no exams are selected, questions are drawn from all available questions.
      </p>

      {/* Question count */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Number of questions in free trial exam
        </label>
        <input
          type="number"
          min={1}
          max={100}
          value={questionCount}
          onChange={(e) => setQuestionCount(Number(e.target.value))}
          className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-400 mt-1">Default: 50</p>
      </div>

      {/* Exam pool selector */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-700">Source exam pool</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {selectedExamIds.length === 0
                ? "All questions (no filter)"
                : `${selectedExamIds.length} exam(s) selected`}
            </p>
          </div>
          {selectedExamIds.length > 0 && (
            <button
              onClick={() => setSelectedExamIds([])}
              className="text-xs text-red-500 hover:text-red-700"
            >
              Clear all
            </button>
          )}
        </div>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {exams.map((exam) => (
            <label
              key={exam.id}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedExamIds.includes(exam.id)}
                onChange={() => toggleExam(exam.id)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-800">{exam.title}</span>
              <span className="ml-auto text-xs text-gray-400">
                {exam.total_questions} questions
              </span>
            </label>
          ))}
        </div>
      </div>

      <button
        onClick={() => saveMutation.mutate()}
        disabled={saveMutation.isPending}
        className="bg-blue-600 text-white font-semibold px-6 py-3 rounded-xl hover:bg-blue-700 transition disabled:opacity-60"
      >
        {saveMutation.isPending ? "Saving…" : "Save Configuration"}
      </button>
    </div>
  );
}
